import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedChartDataSet } from '../Shared/ManagedChart';
import { ManagedDrilldown } from '../Shared/ManagedDrilldown';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapSlimDataSetAdminModel,
    SlimDataSetAdminModel,
 } from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { ColorPicker } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import GetDataSetAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsRequest';
import GetDataSetAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';
import AUDDrilldowns, { ManagedDrilldownEditResult } from './AUDDrilldowns';

export type ManagedChartDataSetEditResult = {
    operation: ManagedAdminChildOperation;
    chartDataSet: ManagedChartDataSet;
};

type AUDChartDataSetFormValues = {
    cdsChartTypeID: number;
    cdsAxis: string;
    cdsLabel: string;
    cdsFormatting: string;
    cdsDataColumn: string;
    cdsReversed: boolean;
    cdsAlternateDataSetID: number;
    cdsAlternateDataSetXColumn: string;
    cdsTooltipSource: string;
    cdsIntervalType: string;
    cdsGroupBySplit: string;
    cdsGroupByDefaultShow: string;
    cdsTooltipCustom: string;
    cdsManualAxisMin: number;
    cdsManualAxisMax: number;
    cdsSeriesColor: string;
};

type AUDChartDataSetsProps = {
    chartID: number;
    chartTitle: string;
    chartDataSets: ManagedChartDataSet[];
    initialSelectedChartDataSetID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetChartDataSet: (
        result: ManagedChartDataSetEditResult
    ) => void;
    close: () => void;
};

const AUDChartDataSets: React.FC<AUDChartDataSetsProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDChartDataSet] = Form.useForm();

    const chartDataSets = props.chartDataSets ?? [];

    const MakeRGBString = (color: string) => {
        if (color === '') {
            return 'rgb(0,0,0)';
        }
        else {
            return 'rgb(' + color + ')';
        }
    }

    const getInitialCDSMode = (): ManagedAdminActionMode  => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedChartDataSet = (): ManagedChartDataSet | undefined => {
        if (!props.initialSelectedChartDataSetID || props.initialSelectedChartDataSetID <= 0) {
            return undefined;
        }

        return chartDataSets.find(
            (chartDataSet) => chartDataSet.id === props.initialSelectedChartDataSetID
        );
    };

    const initialSelectedChartDataSet = getInitialSelectedChartDataSet();
    const initialCDSMode = getInitialCDSMode();

    const initialCDSFormValues = initialCDSMode === 'add'
        ? {
            cdsChartTypeID: 2,
            cdsAxis: 'X',
            cdsLabel: '',
            cdsFormatting: '',
            cdsDataColumn: '',
            cdsReversed: false,
            cdsAlternateDataSetID: 0,
            cdsAlternateDataSetXColumn: '',
            cdsTooltipSource: '',
            cdsIntervalType: '',
            cdsGroupBySplit: '',
            cdsGroupByDefaultShow: '',
            cdsTooltipCustom: '',
            cdsManualAxisMin: 0,
            cdsManualAxisMax: 0,
            cdsSeriesColor: 'rgb(0,0,0)',
        }
        : {
            cdsChartTypeID: initialSelectedChartDataSet?.chartTypeId ?? 2,
            cdsAxis: initialSelectedChartDataSet?.axis ?? 'X',
            cdsLabel: initialSelectedChartDataSet?.label ?? '',
            cdsFormatting: initialSelectedChartDataSet?.formatting ?? '',
            cdsDataColumn: initialSelectedChartDataSet?.dataColumn ?? '',
            cdsReversed: initialSelectedChartDataSet?.reversed ?? false,
            cdsAlternateDataSetID: initialSelectedChartDataSet?.alternateDSId ?? 0,
            cdsAlternateDataSetXColumn: initialSelectedChartDataSet?.alternateDSXColumn ?? '',
            cdsTooltipSource: initialSelectedChartDataSet?.tooltipSource ?? '',
            cdsIntervalType: initialSelectedChartDataSet?.intervalType ?? '',
            cdsGroupBySplit: initialSelectedChartDataSet?.groupBySplit ?? '',
            cdsGroupByDefaultShow: initialSelectedChartDataSet?.groupByDefaultShow ?? '',
            cdsTooltipCustom: initialSelectedChartDataSet?.tooltipCustom ?? '',
            cdsManualAxisMin: initialSelectedChartDataSet?.manualAxisMin ?? 0,
            cdsManualAxisMax: initialSelectedChartDataSet?.manualAxisMax ?? 0,
            cdsSeriesColor: MakeRGBString(initialSelectedChartDataSet?.seriesColor ?? ''),
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialCDSMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialCDSMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialCDSMode === 'delete');

    const [cdsSelectedChartDataSetID, setCDSSelectedChartDataSetID] = useState(initialSelectedChartDataSet?.id ?? -1);
    const [cdsSelectedChartDataSetDataColumn, setCDSSelectedChartDataSetDataColumn] = useState(initialSelectedChartDataSet
            ? `${initialSelectedChartDataSet.label ?? ''} / ${initialSelectedChartDataSet.dataColumn ?? ''}`
            : ''
    );

    const [manageChartDataSetDrilldownsModalOpen, setManageChartDataSetDrilldownsModalOpen] = useState(false);
    const [cdsSelectedDrilldownID, setCDSSelectedDrilldownID] = useState(-1);
    const [cdsDrilldownEditorMode, setCDSDrilldownEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [cdsIncludeDisabled] = useState(false);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const cdsPropsChartID = props.chartID > 0 ? props.chartID : 0;
    const cdsPropsChartTitle = props.chartTitle !== '' ? props.chartTitle : 'New Chart DataSet';

    const setCDSMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getCDSOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getCDSActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const clearCDSSelection = () => {
        setCDSSelectedChartDataSetID(-1);
        setCDSSelectedChartDataSetDataColumn('');
        setManageChartDataSetDrilldownsModalOpen(false);
        setCDSSelectedDrilldownID(-1);
        setCDSDrilldownEditorMode('idle');
        setCDSMode('add');
        handleCDSChartDataSetResetForm('blank');
    };
    
    const getSelectedChartDataSet = (selectedChartDataSetID: number = cdsSelectedChartDataSetID): ManagedChartDataSet | undefined => {
        return chartDataSets.find((chartDataSet) => chartDataSet.id === selectedChartDataSetID);
    };

    const setCDSBlankForm = () => {
        formAUDChartDataSet.setFieldValue('cdsChartTypeID', 2);
        formAUDChartDataSet.setFieldValue('cdsAxis', 'X');
        formAUDChartDataSet.setFieldValue('cdsLabel', '');
        formAUDChartDataSet.setFieldValue('cdsFormatting', '');
        formAUDChartDataSet.setFieldValue('cdsDataColumn', '');
        formAUDChartDataSet.setFieldValue('cdsReversed', false);
        formAUDChartDataSet.setFieldValue('cdsAlternateDataSetID', 0);
        formAUDChartDataSet.setFieldValue('cdsAlternateDataSetXColumn', '');
        formAUDChartDataSet.setFieldValue('cdsTooltipSource', '');
        formAUDChartDataSet.setFieldValue('cdsIntervalType', '');
        formAUDChartDataSet.setFieldValue('cdsGroupBySplit', '');
        formAUDChartDataSet.setFieldValue('cdsGroupByDefaultShow', '');
        formAUDChartDataSet.setFieldValue('cdsTooltipCustom', '');
        formAUDChartDataSet.setFieldValue('cdsManualAxisMin', 0);
        formAUDChartDataSet.setFieldValue('cdsManualAxisMax', 0);

        const defaultSeriesColor = MakeRGBString('');
        formAUDChartDataSet.setFieldValue('cdsSeriesColor', defaultSeriesColor);
    };

    const setCDSFormFromSelectedChartDataSet = () => {
        const selectedChartDataSet = getSelectedChartDataSet();

        formAUDChartDataSet.setFieldValue('cdsChartTypeID', selectedChartDataSet?.chartTypeId ?? 2);
        formAUDChartDataSet.setFieldValue('cdsAxis', selectedChartDataSet?.axis ?? 'X');
        formAUDChartDataSet.setFieldValue('cdsLabel', selectedChartDataSet?.label ?? '');
        formAUDChartDataSet.setFieldValue('cdsFormatting', selectedChartDataSet?.formatting ?? '');
        formAUDChartDataSet.setFieldValue('cdsDataColumn', selectedChartDataSet?.dataColumn ?? '');
        formAUDChartDataSet.setFieldValue('cdsReversed', selectedChartDataSet?.reversed ?? false);
        formAUDChartDataSet.setFieldValue('cdsAlternateDataSetID', selectedChartDataSet?.alternateDSId ?? 0);
        formAUDChartDataSet.setFieldValue('cdsAlternateDataSetXColumn', selectedChartDataSet?.alternateDSXColumn ?? '');
        formAUDChartDataSet.setFieldValue('cdsTooltipSource', selectedChartDataSet?.tooltipSource ?? '');
        formAUDChartDataSet.setFieldValue('cdsIntervalType', selectedChartDataSet?.intervalType ?? '');
        formAUDChartDataSet.setFieldValue('cdsGroupBySplit', selectedChartDataSet?.groupBySplit ?? '');
        formAUDChartDataSet.setFieldValue('cdsGroupByDefaultShow', selectedChartDataSet?.groupByDefaultShow ?? '');
        formAUDChartDataSet.setFieldValue('cdsTooltipCustom', selectedChartDataSet?.tooltipCustom ?? '');
        formAUDChartDataSet.setFieldValue('cdsManualAxisMin', selectedChartDataSet?.manualAxisMin ?? 0);
        formAUDChartDataSet.setFieldValue('cdsManualAxisMax', selectedChartDataSet?.manualAxisMax ?? 0);

        const selectedSeriesColor = MakeRGBString(selectedChartDataSet?.seriesColor ?? '');
        formAUDChartDataSet.setFieldValue('cdsSeriesColor', selectedSeriesColor);
    };

    const handleCDSChartDataSetResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setCDSBlankForm();
            return;
        }

        setCDSFormFromSelectedChartDataSet();
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredCDSField = (fieldName: string): Promise<void> => {
        const value = formAUDChartDataSet.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateCDSDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'cdsChartTypeID':
            case 'cdsDataColumn':
                return validateRequiredCDSField(rule.field);

            case 'cdsAxis':
            case 'cdsLabel':
            case 'cdsFormatting':
            case 'cdsTooltipSource':
            case 'cdsTooltipCustom':
            case 'cdsReversed':
            case 'cdsManualAxisMin':
            case 'cdsManualAxisMax':
            case 'cdsIntervalType':
            case 'cdsGroupBySplit':
            case 'cdsGroupByDefaultShow':
            case 'cdsAlternateDataSetXColumn':
            case 'cdsAlternateDataSetID':
            case 'cdsSeriesColor':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const handleCDSLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message =
            apiError?.response?.data?.message
                ? `${title}: ${apiError.response.data.message}`
                : `${title}: ${String(err)}`;

        console.error(title, err);
        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Error',
                message,
                true
            )
        );
    };

    const handleCDSSeriesColorChange = (color: any) => {
        const returnColor = (!color || color.cleared) ? 'rgb(0,0,0)' : color.toRgbString();
        return returnColor;
    };

    const buildClientChartDataSetKey = (): string => {
        return `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    const StripRGBString = (rgbString: string) => {
        return rgbString.slice(4, -1);
    }

    const buildManagedChartDataSetRequest = (values: AUDChartDataSetFormValues): ManagedChartDataSet => {
        const selectedChartDataSet = getSelectedChartDataSet();

        const clientKey = addButtonClicked ? buildClientChartDataSetKey() : (selectedChartDataSet?.clientKey ?? `server-${cdsSelectedChartDataSetID}`);

        const cdsChartTypeID = Number(
            values.cdsChartTypeID ??
            formAUDChartDataSet.getFieldValue('cdsChartTypeID') ??
            selectedChartDataSet?.chartTypeId ??
            2
        );

        const cdsAlternateDataSetID = values.cdsAlternateDataSetID === 0 ? null : values.cdsAlternateDataSetID;
        const cdsManualAxisMin = Number(values.cdsManualAxisMin);
        const cdsManualAxisMax = Number(values.cdsManualAxisMax);

        return {
            id: addButtonClicked ? 0 : cdsSelectedChartDataSetID,
            clientKey,
            chartId: props.chartID ?? 0,
            chartTypeId: Number.isFinite(cdsChartTypeID) ? cdsChartTypeID : 2,
            axis: values.cdsAxis ?? '',
            label: values.cdsLabel ?? '',
            formatting: values.cdsFormatting ?? '',
            dataColumn: values.cdsDataColumn ?? '',
            reversed: values.cdsReversed ?? false,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (selectedChartDataSet?.createdBy ?? 0),
            modifiedBy: (user?.id ?? 0),
            deletedBy: delButtonClicked ? (user?.id ?? 0) : 0,
            createdTs: addButtonClicked ? new Date() : selectedChartDataSet?.createdTs,
            modifiedTs: new Date(),
            deletedTs: delButtonClicked ? new Date() : undefined,
            alternateDSId: cdsAlternateDataSetID,
            alternateDSXColumn: values.cdsAlternateDataSetXColumn ?? '',
            tooltipSource: values.cdsTooltipSource ?? '',
            intervalType: values.cdsIntervalType ?? '',
            groupBySplit: values.cdsGroupBySplit ?? '',
            groupByDefaultShow: values.cdsGroupByDefaultShow ?? '',
            tooltipCustom: values.cdsTooltipCustom ?? '',
            manualAxisMin: Number.isFinite(cdsManualAxisMin) ? cdsManualAxisMin : 0,
            manualAxisMax: Number.isFinite(cdsManualAxisMax) ? cdsManualAxisMax : 0,
            seriesColor: StripRGBString(values.cdsSeriesColor ?? MakeRGBString('')),
            drilldowns: selectedChartDataSet?.drilldowns ?? [],
        };
    };

    const { data: dataSets = [], isLoading: isGetDataSetsLoading, } = useQuery<GetDataSetAdminModelsResponse[], Error, SlimDataSetAdminModel[]>(
        ['dataSetAdminModels', cdsIncludeDisabled],
        async (): Promise<GetDataSetAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS}`;
                const requestData: GetDataSetAdminModelsRequest = { gdsa_includeDisabled: cdsIncludeDisabled };
                return await AxiosUtil.get<GetDataSetAdminModelsResponse[], GetDataSetAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleCDSLoadError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleCDSLoadError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error', err);
            },
            select: (response: GetDataSetAdminModelsResponse[]): SlimDataSetAdminModel[] =>
                response
                    .filter((eachDataSet) => !eachDataSet.deleted)
                    .map(mapSlimDataSetAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        }
    );

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDChartDataSetFormValues>) => {
        console.error('AUDChartDataSet validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Chart DataSet Validation',
                'Please correct the highlighted Chart DataSet fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDChartDataSetFormValues) => {
        const newManagedChartDataSet = buildManagedChartDataSetRequest(values);

        props.handleSetChartDataSet({
            operation: getCDSOperation(),
            chartDataSet: newManagedChartDataSet,
        });

        close();
    };

    const getChartDataSetDrilldownsEditorContext = () => {
        const selectedChartDataSet = getSelectedChartDataSet();

        return {
            sourceChartDataSetID: cdsSelectedChartDataSetID > 0 ? cdsSelectedChartDataSetID : 0,
            sourceTitle: selectedChartDataSet
                ? `${selectedChartDataSet.label ?? ''} / ${selectedChartDataSet.dataColumn ?? ''}`
                : cdsSelectedChartDataSetDataColumn,
            sourceSubtitle: 'Chart: [' + cdsPropsChartID + '] ' + cdsPropsChartTitle,
            drilldowns: selectedChartDataSet?.drilldowns ?? [],
            initialSelectedDrilldownID: cdsSelectedDrilldownID,
            initialMode: cdsDrilldownEditorMode,
            availableSourceColumns: selectedChartDataSet?.dataColumn ? [selectedChartDataSet.dataColumn] : [],
        };
    };

    const openCDSChartDataSetDrilldownsEditor = (
        mode: ManagedAdminChildOperation,
        selectedDrilldownID: number = -1
    ) => {
        setCDSDrilldownEditorMode(mode);
        setCDSSelectedDrilldownID(selectedDrilldownID);
        setManageChartDataSetDrilldownsModalOpen(true);
    };

    const closeCDSChartDataSetDrilldownsEditor = () => {
        setManageChartDataSetDrilldownsModalOpen(false);
        setCDSDrilldownEditorMode('idle');
        setCDSSelectedDrilldownID(-1);
    };

    const applyDrilldownEditResult = (
        existingDrilldowns: ManagedDrilldown[],
        result: ManagedDrilldownEditResult
    ): ManagedDrilldown[] => {
        const { operation, drilldown } = result;

        switch (operation) {
            case 'add':
                return [...existingDrilldowns, drilldown];

            case 'update':
                return existingDrilldowns.map((eachDrilldown) =>
                    eachDrilldown.id === drilldown.id ? drilldown : eachDrilldown
                );

            case 'delete':
                return existingDrilldowns.filter(
                    (eachDrilldown) => eachDrilldown.id !== drilldown.id
                );

            default:
                return existingDrilldowns;
        }
    };

    const handleSetDrilldowns = (result: ManagedDrilldownEditResult) => {
        const selectedChartDataSet = getSelectedChartDataSet();

        if (!selectedChartDataSet) {
            return;
        }

        props.handleSetChartDataSet({
            operation: 'update',
            chartDataSet: {
                ...selectedChartDataSet,
                drilldowns: applyDrilldownEditResult(selectedChartDataSet.drilldowns, result),
            },
        });
    };

    const onCDSDrilldowns = () => {
        openCDSChartDataSetDrilldownsEditor('add');
    };

    const handleCDSSelectionChange = (value: number) => {
        setCDSSelectedChartDataSetID(value);

        const selectedChartDataSet = getSelectedChartDataSet(value);

        setCDSSelectedChartDataSetDataColumn(
            (selectedChartDataSet?.label ?? '') + ' / ' + (selectedChartDataSet?.dataColumn ?? '')
        );

        setCDSMode('add');
        handleCDSChartDataSetResetForm('blank');
    };

    //=================================================================
    //=================================================================
    
    const handleCDSAddChartDataSet = () => {
        clearCDSSelection();
    };

    //=================================================================
    //=================================================================

    const handleCDSUpdChartDataSet = () => {
        setCDSMode('update');
        handleCDSChartDataSetResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleCDSDelChartDataSet = () => {
        setCDSMode('delete');
        handleCDSChartDataSetResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onCDSCancel = () => {
        clearCDSSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='75%'
                open={manageChartDataSetDrilldownsModalOpen}
                onCancel={closeCDSChartDataSetDrilldownsEditor}
            >
                {
                    manageChartDataSetDrilldownsModalOpen &&
                    (
                        <AUDDrilldowns
                            sourceType='chartDataset'
                            sourceChartDataSetID={getChartDataSetDrilldownsEditorContext().sourceChartDataSetID}
                            sourceTitle={getChartDataSetDrilldownsEditorContext().sourceTitle}
                            sourceSubtitle={getChartDataSetDrilldownsEditorContext().sourceSubtitle}
                            drilldowns={getChartDataSetDrilldownsEditorContext().drilldowns}
                            initialSelectedDrilldownID={getChartDataSetDrilldownsEditorContext().initialSelectedDrilldownID}
                            initialMode={getChartDataSetDrilldownsEditorContext().initialMode}
                            availableSourceColumns={getChartDataSetDrilldownsEditorContext().availableSourceColumns}
                            handleSetDrilldowns={handleSetDrilldowns}
                            close={closeCDSChartDataSetDrilldownsEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={ isGetDataSetsLoading }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Chart DataSets</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Chart: [' + cdsPropsChartID + '] ' + cdsPropsChartTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Chart DataSet: [' + (cdsSelectedChartDataSetID === 0 ? 'new' : cdsSelectedChartDataSetID) + '] ' + cdsSelectedChartDataSetDataColumn}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Chart DataSet to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Chart DataSet'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleCDSSelectionChange}
                        >
                            {chartDataSets.map(
                                (eachChartDataSet) => {
                                    return (
                                        <Option
                                            key={eachChartDataSet.id}
                                            label={eachChartDataSet.dataColumn}
                                            value={eachChartDataSet.id}
                                        >
                                            {eachChartDataSet.dataColumn}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={4}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleCDSAddChartDataSet}
                        >
                            Add Chart DataSet
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={4}>
                        <Button
                            disabled={cdsSelectedChartDataSetID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleCDSUpdChartDataSet}
                        >
                            Update Chart DataSet
                        </Button>
                    </Col>
                    <Col span={2} />
                    <Col span={4}>
                        <Button
                            disabled={cdsSelectedChartDataSetID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleCDSDelChartDataSet}
                        >
                            Delete Chart DataSet
                        </Button>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDChartDataSet}
                    initialValues={initialCDSFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getCDSActionColor() }}>
                                    {addButtonClicked ? 'Add Chart DataSet' :
                                        (updButtonClicked ? 'Update Chart DataSet' :
                                            (delButtonClicked ? 'Delete Chart DataSet' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getCDSActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleCDSChartDataSetResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6}>
                                <div style={{textAlign: 'left'}}>
                                    <Form.Item
                                        key='cdscharttypeid'
                                        label='Chart Type'
                                        labelCol={{ span: 9 }}
                                        name='cdsChartTypeID'
                                        rules={[
                                            {
                                                validator: validateCDSDetailsData,
                                            },
                                        ]}
                                        style={{marginTop: '4px'}}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            showSearch
                                        >
                                            <Option
                                                value={1}
                                                label={'Area'}
                                            >
                                                {'Area'}
                                            </Option>
                                            <Option
                                                value={2}
                                                label={'Column'}
                                            >
                                                {'Column'}
                                            </Option>
                                            <Option
                                                value={3}
                                                label={'Line'}
                                            >
                                                {'Line'}
                                            </Option>
                                            <Option
                                                value={4}
                                                label={'Step Line'}
                                            >
                                                {'Step Line'}
                                            </Option>
                                            <Option
                                                value={5}
                                                label={'Spline'}
                                            >
                                                {'Spline'}
                                            </Option>
                                            <Option
                                                value={6}
                                                label={'Step Area'}
                                            >
                                                {'Step Area'}
                                            </Option>
                                            <Option
                                                value={7}
                                                label={'Spline Area'}
                                            >
                                                {'Spline Area'}
                                            </Option>
                                            <Option
                                                value={8}
                                                label={'Bar'}
                                            >
                                                {'Bar'}
                                            </Option>
                                            <Option
                                                value={9}
                                                label={'Pie'}
                                            >
                                                {'Pie'}
                                            </Option>
                                            <Option
                                                value={10}
                                                label={'Pyramid'}
                                            >
                                                {'Pyramid'}
                                            </Option>
                                            <Option
                                                value={11}
                                                label={'Donut'}
                                            >
                                                {'Donut'}
                                            </Option>
                                            <Option
                                                value={12}
                                                label={'Stacked Column'}
                                            >
                                                {'Stacked Column'}
                                            </Option>
                                            <Option
                                                value={13}
                                                label={'Stacked Bar'}
                                            >
                                                {'Stacked Bar'}
                                            </Option>
                                            <Option
                                                value={14}
                                                label={'US Map - States'}
                                            >
                                                {'US Map - States'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={3}>
                                <div style={{textAlign: 'left'}}>
                                    <Form.Item
                                        key='cdsaxis'
                                        name='cdsAxis'
                                        label='Axis'
                                        labelCol={{ span: 7 }}
                                        style={{marginTop: '4px'}}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            showSearch
                                        >
                                            <Option
                                                value={'X'}
                                                label={'X'}
                                            >
                                                {'X'}
                                            </Option>
                                            <Option
                                                value={'Y'}
                                                label={'Y'}
                                            >
                                                {'Y'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    key='cdslabel'
                                    name='cdsLabel'
                                    label='Label'
                                    labelCol={{ span: 4, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Label'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='cdsformatting'
                                    name='cdsFormatting'
                                    label='Formatting'
                                    labelCol={{ span: 6, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Formatting'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={8}>
                                <Form.Item
                                    key='cdsdatacolumn'
                                    name='cdsDataColumn'
                                    label='Data Column'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            message: 'Data Column required.',
                                            required: true,
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Data Column'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='cdstooltipsource'
                                    label='Tooltip Source'
                                    labelCol={{ span: 8, }}
                                    name='cdsTooltipSource'
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Tooltip Source'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='cdstooltipcustom'
                                    label='Tooltip Custom'
                                    labelCol={{ span: 8, }}
                                    name='cdsTooltipCustom'
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Tooltip Custom'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={3}>
                                <Form.Item
                                    key='cdsreversed'
                                    name='cdsReversed'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'left', justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Reversed
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    key='cdsseriescolor'
                                    name='cdsSeriesColor'
                                    label='Series Color'
                                    getValueFromEvent={(color, rgb) => handleCDSSeriesColorChange(color)}
                                    style={{display: 'flex', alignItems: 'left', justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <ColorPicker
                                        allowClear
                                        disabledAlpha
                                        format={'rgb'}
                                    >
                                    </ColorPicker>
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item
                                    key='cdsmanualaxismin'
                                    name='cdsManualAxisMin'
                                    label='Manual Axis Min'
                                    labelCol={{ span: 13, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Manual Axis Min'
                                        style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '75%'}}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item
                                    key='cdsmanualaxismax'
                                    name='cdsManualAxisMax'
                                    label='Manual Axis Max'
                                    labelCol={{ span: 13, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Manual Axis Max'
                                        style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '75%'}}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <div style={{textAlign: 'left'}}>
                                    <Form.Item
                                        key='cdsintervaltype'
                                        name='cdsIntervalType'
                                        label='Interval Type'
                                        labelCol={{ span: 10 }}
                                        style={{ marginTop: '4px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            showSearch
                                        >
                                            <Option
                                                value={'number'}
                                                label={'Number'}
                                            >
                                                {'Number'}
                                            </Option>
                                            <Option
                                                value={'millisecond'}
                                                label={'Millisecond'}
                                            >
                                                {'Millisecond'}
                                            </Option>
                                            <Option
                                                value={'second'}
                                                label={'Second'}
                                            >
                                                {'Second'}
                                            </Option>
                                            <Option
                                                value={'minute'}
                                                label={'Minute'}
                                            >
                                                {'Minute'}
                                            </Option>
                                            <Option
                                                value={'hour'}
                                                label={'Hour'}
                                            >
                                                {'Hour'}
                                            </Option>
                                            <Option
                                                value={'day'}
                                                label={'Day'}
                                            >
                                                {'Day'}
                                            </Option>
                                            <Option
                                                value={'month'}
                                                label={'Month'}
                                            >
                                                {'Month'}
                                            </Option>
                                            <Option
                                                value={'year'}
                                                label={'Year'}
                                            >
                                                {'Year'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={8}>
                                <Form.Item
                                    key='cdsgroupbysplit'
                                    name='cdsGroupBySplit'
                                    label='Group By Split'
                                    labelCol={{ span: 10, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Group By Split'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='cdsgroupbydefaultshow'
                                    name='cdsGroupByDefaultShow'
                                    label='Group By Def. Show'
                                    labelCol={{ span: 10, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Group By Def. Show'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='cdsalternatedatasetxcolumn'
                                    name='cdsAlternateDataSetXColumn'
                                    label='Alt. DS X Column'
                                    labelCol={{ span: 10, }}
                                    rules={[
                                        {
                                            validator: validateCDSDetailsData,
                                        },
                                    ]}
                                    style={{marginTop: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Alternate DS X Column'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign: 'left'}}>
                                    <Form.Item
                                        name='cdsAlternateDataSetID'
                                        label='Alternate DataSet'
                                        labelCol={{ span: 7 }}
                                        rules={[
                                            {
                                                validator: validateCDSDetailsData,
                                            },
                                        ]}
                                        style={{ marginTop: '4px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            optionFilterProp='label'
                                            showSearch
                                        >
                                            <Option
                                                key={0}
                                                label={'(Not Set)'}
                                                value={0}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {dataSets.map(
                                                (eachDataSet) => {
                                                    return (
                                                        <Option
                                                            key={eachDataSet.id}
                                                            label={eachDataSet.name}
                                                            value={eachDataSet.id}
                                                        >
                                                            {eachDataSet.name}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2} />
                            <Col span={8}>
                                <Form.Item
                                    key='cdsdetailsdrilldowns'
                                    name='cdsDetailsDrilldowns'
                                    style={{marginBottom: '24px'}}
                                >
                                    <Button
                                        disabled={cdsSelectedChartDataSetID === -1}
                                        style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                        onClick={onCDSDrilldowns}
                                    >
                                        Chart DataSet Drilldowns
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={4}>
                            <Form.Item
                                name='cdsCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={12} />
                        <Col span={4}>
                            <Form.Item
                                name='cdsApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Chart DataSet' : (updButtonClicked ? 'Update Chart DataSet' : (delButtonClicked ? 'Delete Chart DataSet' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name='cdsCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onCDSCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDChartDataSets;
